/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import useActiveProject from '@/hooks/useActiveProject';
import {
  Inbox, Sparkles, ChevronRight, ChevronDown, Upload, Send, Loader2,
  CheckCircle2, X, Image as ImageIcon, Film, Folder, FolderOpen,
  AlertTriangle, Search, Plus,
} from 'lucide-react';
import { formatUploadError } from '@/lib/uploadError';
import EpisodeLineupModal from '@/components/EpisodeLineupModal';

/**
 * Submit-for-Approval Tree
 * ────────────────────────
 * 2-pane layout that mirrors the Approvals Inbox UX:
 *   Left  → 11-department buckets (Script/Storyboard/Concept Art/...)
 *   Right → Project ▸ Episode ▸ Item list (from screenplay ingest)
 *           with a per-item upload + SUBMIT button.
 *
 * Used by: master_admin · production_manager · supervisor · creative_director.
 */

// 11 client-facing buckets — each maps to one or more 14-dept task_types.
// Stage code is what /reviews/submit accepts.
// `lineupEligible: true` enables the EpisodeLineupModal CTA on that bucket
// (founder spec Feb 2026 — clients review the full assembled cut, not
// individual shots, for these long-form-output departments).
const BUCKETS = [
  { key: 'script',     label: 'Script',                color: '#60a5fa', taskTypes: ['script'],                     stage: 'script',     icon: '■' },
  { key: 'storyboard', label: 'Storyboard / Animatics', color: '#a78bfa', taskTypes: ['storyboard', 'animatics'],   stage: 'storyboard', icon: '■' },
  { key: 'concept_art', label: 'Concept Art',          color: '#f472b6', taskTypes: ['concept_art'],                stage: 'concept_art', icon: '■' },
  { key: 'modeling',   label: 'Asset Build',            color: '#10b981', taskTypes: ['modeling', 'texturing', 'asset_build'],     stage: 'modeling',   icon: '■' },
  { key: 'rigging',    label: 'Rigging',                color: '#14b8a6', taskTypes: ['rigging'],                   stage: 'rigging',    icon: '■' },
  { key: 'animation',  label: 'Layout & Animation',     color: '#f59e0b', taskTypes: ['layout', 'animation', 'fx'], stage: 'animation',  icon: '■', lineupEligible: true },
  { key: 'lighting',   label: 'Lighting',               color: '#fb923c', taskTypes: ['lighting'],                  stage: 'lighting',   icon: '■' },
  { key: 'rendering',  label: 'Rendering',              color: '#ec4899', taskTypes: ['render'],                    stage: 'rendering',  icon: '■' },
  { key: 'comp',       label: 'Comp & Edit',            color: '#84cc16', taskTypes: ['comp'],                      stage: 'comp',       icon: '■' },
  { key: 'sound',      label: 'Sound & Music',          color: '#eab308', taskTypes: ['sound'],                     stage: 'sound',      icon: '■' },
  { key: 'delivery',   label: 'Final Delivery',         color: '#ef4444', taskTypes: [],                            stage: 'delivery',   icon: '■' },
];

export default function SubmitForApprovalTreePage() {
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useActiveProject(user?.id);
  const [pipeline, setPipeline] = useState(null);
  const [loading, setLoading] = useState(true);
  const [bucketKey, setBucketKey] = useState('concept_art');
  const [search, setSearch] = useState('');
  const [openItem, setOpenItem] = useState(null); // {parent_code, parent_kind, items:[task]}
  // Fast lookup of the latest submission state for each task in the current
  // project — key by task_id (which is what we send as item_refs[]). Powers
  // the row-level "APPROVAL PENDING / APPROVED" pill so PMs can see status
  // without expanding every row.
  const [submissionByTaskId, setSubmissionByTaskId] = useState({});

  const isAllowed = ['master_admin', 'production_manager', 'supervisor', 'creative_director'].includes(user?.role);

  useEffect(() => {
    api.get('/projects').then(r => {
      setProjects(r.data || []);
    });
  }, []);

  const loadSubmissions = async (pid) => {
    if (!pid) { setSubmissionByTaskId({}); return; }
    try {
      const r = await api.get(`/reviews/my-submissions?project_id=${pid}`);
      const map = {};
      for (const s of (r.data?.items || [])) {
        for (const tid of (s.item_refs || [])) {
          // Keep the most recent submission per task (sort already DESC by submitted_at)
          if (!map[tid]) map[tid] = s;
        }
      }
      setSubmissionByTaskId(map);
    } catch { setSubmissionByTaskId({}); }
  };

  useEffect(() => {
    if (!projectId) return;
    setLoading(true);
    Promise.all([
      api.get(`/projects/${projectId}/submission-pipeline`).then(r => setPipeline(r.data)).catch(() => setPipeline(null)),
      loadSubmissions(projectId),
    ]).finally(() => setLoading(false));
    // eslint-disable-next-line
  }, [projectId]);

  const bucket = BUCKETS.find(b => b.key === bucketKey) || BUCKETS[0];
  const project = projects.find(p => p.id === projectId);
  const [kindFilter, setKindFilter] = useState('all'); // all | character | background | prop | shot | project
  const [showAddAsset, setShowAddAsset] = useState(false);
  // Founder spec Feb 2026: on Layout & Animation (and other lineup-
  // eligible buckets) admin/PM can submit ONE full-episode MP4 instead
  // of per-shot tracking. Opens EpisodeLineupModal.
  const [showLineup, setShowLineup] = useState(false);

  const refreshPipeline = async () => {
    if (!projectId) return;
    try {
      const r = await api.get(`/projects/${projectId}/submission-pipeline`);
      setPipeline(r.data);
    } catch { /* keep existing */ }
  };

  // Build hierarchical project picker: Shows → Episodes nested + standalone projects flat.
  // Empirically the dropdown was confusing — episodes appeared flat alongside their parent
  // Show with no visual link, and selecting a Show showed an empty pipeline (because data
  // lives in episodes). Now: <optgroup label="FairyTales (Show)"> wraps its episodes;
  // selecting a Show auto-jumps to the Show's first episode.
  const projectGroups = useMemo(() => {
    const shows = projects.filter(p => p.is_show);
    const byParent = new Map(); // show_id → [episodes]
    for (const ep of projects.filter(p => p.parent_project_id)) {
      const arr = byParent.get(ep.parent_project_id) || [];
      arr.push(ep);
      byParent.set(ep.parent_project_id, arr);
    }
    // Stable sort by episode_number — guarantees same order for every user.
    for (const arr of byParent.values()) {
      arr.sort((a, b) => {
        const an = a.episode_number ?? 9999;
        const bn = b.episode_number ?? 9999;
        if (an !== bn) return an - bn;
        return (a.created_at || '').localeCompare(b.created_at || '');
      });
    }
    const standalone = projects.filter(p => !p.is_show && !p.parent_project_id);
    return { shows, byParent, standalone };
  }, [projects]);

  const handleProjectChange = (newId) => {
    const picked = projects.find(p => p.id === newId);
    // If the user selected a Show, auto-redirect to its first episode
    // (Shows are containers — they have no pipeline data of their own).
    if (picked?.is_show) {
      const eps = projectGroups.byParent.get(picked.id) || [];
      if (eps.length > 0) {
        setProjectId(eps[0].id);
        return;
      }
    }
    setProjectId(newId);
  };

  // Pre-select the first NON-SHOW project on first load (so the user lands on a project
  // that actually has data, not on an empty Show). Also self-heals if the persisted
  // active project id no longer exists (e.g. project was deleted in another tab).
  useEffect(() => {
    if (!projects.length) return;
    const exists = projectId && projects.find(p => p.id === projectId);
    if (exists) return;
    const firstReal = projects.find(p => !p.is_show);
    if (firstReal) setProjectId(firstReal.id);
    else if (projects.length) setProjectId(projects[0].id);
  }, [projects, projectId]);

  // Per-bucket tasks aggregated from all matching task_types,
  // grouped by parent (CH_hansel, BG_poor_cottage, SH_001_0001, project) for tree view.
  const grouped = useMemo(() => {
    if (!pipeline) return [];
    const tasks = bucket.taskTypes.flatMap(t => (pipeline.tasks_by_department?.[t] || []).map(x => ({ ...x, _type: t })));
    const map = new Map();
    for (const t of tasks) {
      const key = t.parent_code || t.parent_kind || 'project';
      if (!map.has(key)) map.set(key, { parent_code: t.parent_code, parent_kind: t.parent_kind, items: [] });
      map.get(key).items.push(t);
    }
    let arr = Array.from(map.values());
    if (kindFilter !== 'all') {
      arr = arr.filter(g => (g.parent_kind || 'project') === kindFilter);
    }
    if (search) {
      arr = arr.filter(g =>
        (g.parent_code || '').toLowerCase().includes(search.toLowerCase()) ||
        g.items.some(i => (i.title || '').toLowerCase().includes(search.toLowerCase()))
      );
    }
    return arr.sort((a, b) => (a.parent_code || '').localeCompare(b.parent_code || ''));
  }, [pipeline, bucket, search, kindFilter]);

  // Counts of asset kinds for the sub-tab strip (independent of kindFilter so users
  // always see how many of each kind exist for the current bucket).
  const kindCounts = useMemo(() => {
    if (!pipeline) return { character: 0, background: 0, prop: 0, shot: 0, project: 0 };
    const tasks = bucket.taskTypes.flatMap(t => pipeline.tasks_by_department?.[t] || []);
    const seen = { character: new Set(), background: new Set(), prop: new Set(), shot: new Set(), project: new Set() };
    for (const t of tasks) {
      const k = t.parent_kind || 'project';
      const code = t.parent_code || 'project';
      if (seen[k]) seen[k].add(code);
    }
    return Object.fromEntries(Object.entries(seen).map(([k, v]) => [k, v.size]));
  }, [pipeline, bucket]);

  // Bucket counts for the left rail
  const counts = useMemo(() => {
    if (!pipeline) return {};
    const out = {};
    for (const b of BUCKETS) {
      out[b.key] = b.taskTypes.reduce((a, t) => a + (pipeline.totals?.[t] || 0), 0);
    }
    return out;
  }, [pipeline]);

  if (!isAllowed) {
    return (
      <div className="text-center py-16 text-zinc-500 text-sm" data-testid="submit-tree-forbidden">
        Only Admins, Production Managers, Supervisors and Creative Directors can use the Submit for Approval tree.
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="submit-tree-page">
      {/* Hero */}
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-md bg-gradient-to-br from-amber-400 to-rose-500 flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-zinc-950" />
            </div>
            <h1 className="text-2xl font-heading font-bold text-white">Submit for Approval</h1>
            <span className="px-2 py-0.5 text-[9px] uppercase tracking-wider bg-amber-500/15 text-amber-300 rounded">PM workflow</span>
          </div>
          <p className="text-xs text-zinc-500 mt-1">
            Pick a department → drill into the project → upload media for any item → press SUBMIT to push it straight to the client.
          </p>
        </div>
        <select value={projectId || ''} onChange={e => handleProjectChange(e.target.value)} data-testid="submit-project-select"
          className="h-8 bg-zinc-900 border border-zinc-800 rounded-sm text-xs text-zinc-200 px-3 focus:outline-none min-w-[14rem]">
          {!projectId && (
            <option value="" disabled>{projects.length === 0 ? 'Loading projects…' : 'Select a project…'}</option>
          )}
          {/* Standalone projects (flat) */}
          {projectGroups.standalone.length > 0 && (
            <optgroup label="── Standalone Projects ──">
              {projectGroups.standalone.map(p => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </optgroup>
          )}
          {/* Each Show as an <optgroup> wrapping its episodes */}
          {projectGroups.shows.map(show => {
            const eps = projectGroups.byParent.get(show.id) || [];
            return (
              <optgroup key={show.id} label={`📺 ${show.name} (Show — pick an episode)`}>
                {/* The Show row itself — selecting it auto-jumps to first episode */}
                <option value={show.id} disabled={eps.length === 0}>
                  ↳ all episodes ({eps.length})
                </option>
                {eps.map((ep, i) => (
                  <option key={ep.id} value={ep.id}>
                    &nbsp;&nbsp;&nbsp;EP{String(ep.episode_number ?? (i + 1)).padStart(2, '0')} · {ep.name}
                  </option>
                ))}
              </optgroup>
            );
          })}
          {/* Orphan episodes (parent_project_id pointing to deleted/missing show) */}
          {(() => {
            const all = new Set(projects.map(p => p.id));
            const orphans = projects.filter(p => p.parent_project_id && !all.has(p.parent_project_id));
            return orphans.length > 0 ? (
              <optgroup label="── Orphan Episodes ──">
                {orphans.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </optgroup>
            ) : null;
          })()}
        </select>
      </div>

      {/* Show-selected guard banner — defence in depth in case auto-redirect didn't fire */}
      {project?.is_show && (
        <div className="bg-violet-500/5 border border-violet-500/30 rounded-sm p-3 flex items-start gap-3" data-testid="show-selected-banner">
          <span className="text-violet-300 text-base leading-none mt-0.5">📺</span>
          <div className="flex-1">
            <p className="text-xs font-medium text-violet-100">{project.name} is a Show — pick an episode</p>
            <p className="text-[10px] text-zinc-400 mt-0.5">Shows are containers; the screenplay, MDL, shots and tasks all live inside individual episodes.</p>
            <div className="flex flex-wrap gap-1.5 mt-2">
              {(projectGroups.byParent.get(project.id) || []).map((ep, i) => (
                <button key={ep.id} onClick={() => setProjectId(ep.id)} data-testid={`show-banner-ep-${i}`}
                  className="text-[10px] px-2.5 py-1 rounded bg-violet-500/15 hover:bg-violet-500/30 text-violet-200 border border-violet-500/30">
                  EP{String(ep.episode_number ?? (i + 1)).padStart(2, '0')} · {ep.name}
                </button>
              ))}
              {(projectGroups.byParent.get(project.id) || []).length === 0 && (
                <span className="text-[10px] text-zinc-500 italic">No episodes yet — create them in Projects → {project.name} → Add Episode.</span>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="flex gap-3" data-testid="submit-tree-layout">
        {/* LEFT: Department rail */}
        <aside className="w-60 flex-shrink-0 bg-zinc-900 border border-zinc-800 rounded-sm">          <div className="px-3 py-2 border-b border-zinc-800">
            <p className="text-[9px] text-zinc-500 uppercase tracking-wider">Departments</p>
          </div>
          <div className="py-1">
            <button onClick={() => setBucketKey('all')} data-testid="submit-bucket-all"
              className={`w-full text-left px-3 py-2 text-[11px] flex items-center justify-between ${bucketKey === 'all' ? 'bg-zinc-800 text-white' : 'text-zinc-400 hover:bg-zinc-800/40'}`}>
              <span className="flex items-center gap-2"><Sparkles className="w-3 h-3" /> All departments</span>
              <span className="text-[9px] text-zinc-500 font-mono">{Object.values(counts).reduce((a, b) => a + b, 0)}</span>
            </button>
            {BUCKETS.map(b => {
              const n = counts[b.key] || 0;
              const isActive = bucketKey === b.key;
              return (
                <button key={b.key} onClick={() => setBucketKey(b.key)} data-testid={`submit-bucket-${b.key}`}
                  className={`w-full text-left px-3 py-2 text-[11px] flex items-center justify-between ${isActive ? 'bg-zinc-800 text-white' : 'text-zinc-400 hover:bg-zinc-800/40'}`}>
                  <span className="flex items-center gap-2 min-w-0">
                    <span className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ backgroundColor: b.color }} />
                    <span className="truncate">{b.label}</span>
                  </span>
                  {n > 0 ? (
                    <span className="text-[9px] px-1.5 py-0.5 rounded bg-zinc-800 font-mono" style={{ color: b.color }}>{n}</span>
                  ) : (
                    <span className="text-[9px] text-zinc-700">0</span>
                  )}
                </button>
              );
            })}
          </div>
        </aside>

        {/* RIGHT: Tree + per-item submission cards */}
        <main className="flex-1 bg-zinc-900 border border-zinc-800 rounded-sm flex flex-col min-h-[28rem]">
          {/* Breadcrumb */}
          <div className="px-4 py-2.5 border-b border-zinc-800 flex items-center gap-2" data-testid="submit-breadcrumb">
            <Inbox className="w-3.5 h-3.5 text-amber-400" />
            <span className="text-[11px] text-zinc-300 font-medium">Submit for Approval</span>
            <ChevronRight className="w-3 h-3 text-zinc-700" />
            <span className="text-[11px]" style={{ color: bucket.color }}>{bucketKey === 'all' ? 'All departments' : bucket.label}</span>
            <ChevronRight className="w-3 h-3 text-zinc-700" />
            <span className="text-[11px] text-zinc-300">{project?.name || '—'}</span>
            <div className="ml-auto relative w-56">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-zinc-500" />
              <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search shot or character..."
                data-testid="submit-search"
                className="w-full h-7 pl-7 pr-2 bg-zinc-950 border border-zinc-800 rounded-sm text-[11px] text-white focus:outline-none" />
            </div>
          </div>

          {/* Founder spec Feb 2026: on long-form-output buckets (Layout &
              Animation today, more later) the client doesn't want to
              review individual shots — they want the FULL assembled
              episode MP4 reviewed in one timeline. This CTA opens a
              dedicated upload modal that bypasses the per-shot tree. */}
          {bucket.lineupEligible && projectId && !project?.is_show && (
            <div className="px-3 py-2.5 border-b border-zinc-800 bg-gradient-to-r from-amber-950/30 to-zinc-900 flex items-center justify-between gap-3" data-testid="episode-lineup-cta-bar">
              <div className="flex items-start gap-2.5 min-w-0">
                <div className="w-7 h-7 rounded bg-gradient-to-br from-amber-400 to-rose-500 flex items-center justify-center flex-shrink-0">
                  <Film className="w-3.5 h-3.5 text-zinc-950" />
                </div>
                <div className="min-w-0">
                  <p className="text-[11px] text-amber-100 font-medium">
                    Send the FULL episode lineup to client review
                  </p>
                  <p className="text-[10px] text-amber-300/70 leading-relaxed">
                    Upload one MP4 (≤ 500 MB recommended) — the client reviews like Animatics, with timeline pin annotations &amp; comments. Skips per-shot tracking.
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowLineup(true)}
                data-testid="episode-lineup-open-btn"
                className="flex-shrink-0 px-3 py-1.5 rounded text-[11px] font-medium bg-gradient-to-r from-amber-400 to-rose-500 hover:from-amber-300 hover:to-rose-400 text-zinc-950 flex items-center gap-1.5"
              >
                <Upload className="w-3 h-3" /> Upload Episode Lineup
              </button>
            </div>
          )}

          {/* Asset-kind sub-tabs (Characters · Environments · Props · Shots · Project) */}
          <div className="px-3 py-2 border-b border-zinc-800 flex items-center gap-1 flex-wrap" data-testid="submit-kind-tabs">
            {[
              { k: 'all',        l: 'All',           c: 'text-zinc-200', n: Object.values(kindCounts).reduce((a, b) => a + b, 0) },
              { k: 'character',  l: 'Characters',    c: 'text-rose-300', n: kindCounts.character },
              { k: 'background', l: 'Environments',  c: 'text-cyan-300', n: kindCounts.background },
              { k: 'prop',       l: 'Props',         c: 'text-amber-300', n: kindCounts.prop },
              { k: 'shot',       l: 'Shots',         c: 'text-violet-300', n: kindCounts.shot },
              { k: 'project',    l: 'Project-level', c: 'text-emerald-300', n: kindCounts.project },
            ].map(t => {
              const active = kindFilter === t.k;
              return (
                <button key={t.k} onClick={() => setKindFilter(t.k)}
                  data-testid={`submit-kind-${t.k}`}
                  className={`text-[11px] px-2.5 py-1 rounded border transition ${
                    active
                      ? 'bg-zinc-800 border-zinc-600 ' + t.c
                      : 'bg-zinc-950 border-zinc-800 text-zinc-500 hover:text-zinc-200 hover:border-zinc-700'
                  }`}>
                  {t.l} <span className="ml-1 font-mono text-[9px] text-zinc-500">{t.n}</span>
                </button>
              );
            })}
            {/* Inline Add Asset CTA — opens modal to add Character / Environment / Prop manually
                without re-uploading the breakdown XLSX. */}
            <button
              onClick={() => setShowAddAsset(true)}
              disabled={!projectId || project?.is_show}
              data-testid="submit-add-asset-btn"
              className="ml-auto text-[11px] px-2.5 py-1 rounded border border-emerald-500/40 bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/20 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1"
              title={project?.is_show ? 'Pick an episode first' : 'Add a missing Character / Environment / Prop'}>
              <Plus className="w-3 h-3" /> Add Asset
            </button>
          </div>

          {loading ? (
            <div className="flex-1 flex items-center justify-center text-zinc-500 text-xs" data-testid="submit-loading">
              <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading screenplay items...
            </div>
          ) : !pipeline || grouped.length === 0 ? (
            <EmptyState bucket={bucket} project={project} />
          ) : (
            <div className="flex-1 overflow-y-auto p-3 space-y-2" data-testid="submit-items-list">
              {grouped.map(group => (
                <ParentGroup key={group.parent_code || 'root'} group={group} bucket={bucket}
                  api={api} projectId={projectId} project={project} user={user} onOpen={setOpenItem} openItem={openItem}
                  submissionByTaskId={submissionByTaskId} onSubmissionChange={() => loadSubmissions(projectId)} />
              ))}
            </div>
          )}
        </main>
      </div>

      {/* Founder spec Feb 2026 — Layout & Animation episode lineup
          (full assembled MP4 → client review). Mounted only when the
          admin clicks the lineup CTA on a lineup-eligible bucket. */}
      {showLineup && (
        <EpisodeLineupModal
          api={api}
          projectId={projectId}
          projectName={project?.name || ''}
          bucketStage={bucket.stage}
          bucketLabel={bucket.label}
          canSkipInternal={['master_admin', 'production_manager', 'supervisor', 'creative_director'].includes(user?.role)}
          onClose={() => setShowLineup(false)}
          onSubmitted={async () => {
            // Keep the modal open so the user sees the success state +
            // can submit another lineup (e.g. revised cut) without
            // re-opening. They close manually.
            await loadSubmissions(projectId);
          }}
        />
      )}

      {/* Add Asset modal */}
      {showAddAsset && (
        <AddAssetModal
          api={api}
          projectId={projectId}
          projectName={project?.name || ''}
          defaultCategory={kindFilter === 'background' ? 'background' : kindFilter === 'character' ? 'character' : kindFilter === 'prop' ? 'prop' : 'character'}
          onClose={() => setShowAddAsset(false)}
          onCreated={async () => {
            setShowAddAsset(false);
            await refreshPipeline();
          }}
        />
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// AddAssetModal — manually create a single MDL asset (Character / Env / Prop)
// without re-uploading the breakdown XLSX. Spawns asset-pipeline tasks via the
// /master-library/asset endpoint.
// ────────────────────────────────────────────────────────────────────────────
const AddAssetModal = ({ api, projectId, projectName, defaultCategory, onClose, onCreated }) => {
  const [name, setName] = useState('');
  const [category, setCategory] = useState(defaultCategory || 'character');
  const [description, setDescription] = useState('');
  const [technicalNotes, setTechnicalNotes] = useState('');
  const [complexity, setComplexity] = useState('Medium');
  const [rigRequired, setRigRequired] = useState(true); // default for character
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const CATS = [
    { k: 'character',  l: 'Character',   c: 'text-rose-300',   bg: 'bg-rose-500/15',   br: 'border-rose-500/40' },
    { k: 'background', l: 'Environment', c: 'text-cyan-300',   bg: 'bg-cyan-500/15',   br: 'border-cyan-500/40' },
    { k: 'prop',       l: 'Prop',        c: 'text-amber-300',  bg: 'bg-amber-500/15',  br: 'border-amber-500/40' },
  ];

  const handleSubmit = async () => {
    if (!name.trim()) { setError('Asset name is required'); return; }
    setError('');
    setSubmitting(true);
    try {
      await api.post(`/projects/${projectId}/master-library/asset`, {
        name: name.trim(),
        category,
        description: description.trim(),
        technical_notes: technicalNotes.trim(),
        technical_complexity: complexity,
        rig_required: category === 'character' ? rigRequired : false,
      });
      onCreated();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to create asset');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm" data-testid="add-asset-modal">
      <div className="bg-zinc-900 border border-zinc-700 rounded-md w-full max-w-lg shadow-2xl">
        <div className="px-5 py-3 border-b border-zinc-800 flex items-center justify-between">
          <div>
            <p className="text-[10px] uppercase tracking-wider text-emerald-300">Add asset to MDL</p>
            <h3 className="text-sm font-medium text-white">{projectName}</h3>
          </div>
          <button onClick={onClose} data-testid="add-asset-close" className="text-zinc-500 hover:text-white">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {/* Category pills */}
          <div>
            <label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5 block">Category</label>
            <div className="flex gap-2">
              {CATS.map(c => {
                const active = category === c.k;
                return (
                  <button key={c.k} onClick={() => setCategory(c.k)}
                    data-testid={`add-asset-cat-${c.k}`}
                    className={`flex-1 text-xs px-3 py-2 rounded border transition ${active ? `${c.bg} ${c.br} ${c.c}` : 'bg-zinc-950 border-zinc-800 text-zinc-500 hover:text-zinc-200'}`}>
                    {c.l}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5 block">Asset name *</label>
            <input value={name} onChange={e => setName(e.target.value)}
              data-testid="add-asset-name"
              placeholder={category === 'character' ? 'Prince Eric' : category === 'background' ? 'Underwater Palace' : 'Magic Wand'}
              className="w-full bg-zinc-950 border border-zinc-800 rounded text-sm text-white px-3 py-2 focus:outline-none focus:border-zinc-600" />
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5 block">Visual description</label>
            <textarea value={description} onChange={e => setDescription(e.target.value)}
              data-testid="add-asset-description"
              rows={2}
              placeholder="Brief visual description (optional)"
              className="w-full bg-zinc-950 border border-zinc-800 rounded text-xs text-white px-3 py-2 focus:outline-none focus:border-zinc-600 resize-none" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5 block">Complexity</label>
              <select value={complexity} onChange={e => setComplexity(e.target.value)}
                data-testid="add-asset-complexity"
                className="w-full h-8 bg-zinc-950 border border-zinc-800 rounded text-xs text-white px-2 focus:outline-none">
                <option value="Low">Low</option>
                <option value="Medium">Medium</option>
                <option value="High">High</option>
              </select>
            </div>
            {category === 'character' && (
              <div>
                <label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5 block">Rig required</label>
                <label className="flex items-center gap-2 h-8">
                  <input type="checkbox" checked={rigRequired} onChange={e => setRigRequired(e.target.checked)}
                    data-testid="add-asset-rig"
                    className="accent-emerald-500" />
                  <span className="text-xs text-zinc-300">{rigRequired ? 'Yes — rigging task created' : 'No — concept/model/texture only'}</span>
                </label>
              </div>
            )}
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5 block">Technical notes</label>
            <textarea value={technicalNotes} onChange={e => setTechnicalNotes(e.target.value)}
              data-testid="add-asset-notes"
              rows={2}
              placeholder="Anything the artist needs to know (optional)"
              className="w-full bg-zinc-950 border border-zinc-800 rounded text-xs text-white px-3 py-2 focus:outline-none focus:border-zinc-600 resize-none" />
          </div>

          {error && (
            <div className="bg-rose-500/10 border border-rose-500/40 rounded text-xs text-rose-200 px-3 py-2 flex items-start gap-2" data-testid="add-asset-error">
              <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}
        </div>

        <div className="px-5 py-3 border-t border-zinc-800 flex items-center justify-end gap-2">
          <button onClick={onClose} disabled={submitting}
            className="text-xs px-3 py-1.5 rounded bg-zinc-800 hover:bg-zinc-700 text-zinc-300 disabled:opacity-50">
            Cancel
          </button>
          <button onClick={handleSubmit} disabled={submitting || !name.trim()}
            data-testid="add-asset-submit"
            className="text-xs px-4 py-1.5 rounded bg-emerald-500 hover:bg-emerald-400 text-zinc-950 font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2">
            {submitting && <Loader2 className="w-3 h-3 animate-spin" />}
            {submitting ? 'Creating…' : 'Create asset + tasks'}
          </button>
        </div>
      </div>
    </div>
  );
};

const EmptyState = ({ bucket, project }) => (
  <div className="flex-1 flex flex-col items-center justify-center text-center p-8">
    <Folder className="w-10 h-10 text-zinc-700 mb-3" />
    <p className="text-sm text-zinc-400 mb-1">No <span style={{ color: bucket.color }}>{bucket.label}</span> items yet for {project?.name || 'this project'}</p>
    <p className="text-[11px] text-zinc-600">Upload a screenplay via Allotment → Submission Pipeline tab to populate this list.</p>
  </div>
);

const ParentGroup = ({ group, bucket, api, projectId, project, user, onOpen, openItem, submissionByTaskId, onSubmissionChange }) => {
  const [expanded, setExpanded] = useState(true);
  const parentLabel = group.parent_code || (group.parent_kind === 'project' ? 'Project-level' : 'General');
  const isShot = group.parent_kind === 'shot';
  const isChar = group.parent_kind === 'character';
  const isBg = group.parent_kind === 'background';
  return (
    <div className="bg-zinc-950 border border-zinc-800 rounded-sm" data-testid={`group-${group.parent_code || 'root'}`}>
      <button onClick={() => setExpanded(e => !e)}
        className="w-full px-3 py-2 flex items-center gap-2 hover:bg-zinc-800/40">
        {expanded ? <ChevronDown className="w-3.5 h-3.5 text-amber-400" /> : <ChevronRight className="w-3.5 h-3.5 text-zinc-600" />}
        {expanded ? <FolderOpen className="w-3.5 h-3.5" style={{ color: bucket.color }} /> : <Folder className="w-3.5 h-3.5" style={{ color: bucket.color }} />}
        <span className="text-xs text-zinc-200 font-mono">{parentLabel}</span>
        <span className="text-[9px] uppercase px-1.5 py-0.5 rounded bg-zinc-800 text-zinc-500">{isShot ? 'shot' : isChar ? 'character' : isBg ? 'background' : 'project'}</span>
        <span className="ml-auto text-[10px] text-zinc-500">{group.items.length} task{group.items.length !== 1 ? 's' : ''}</span>
      </button>
      {expanded && (
        <div className="border-t border-zinc-800/40">
          {group.items.map(item => (
            <ItemRow key={item.id} item={item} bucket={bucket} api={api} projectId={projectId} project={project} user={user}
              isOpen={openItem === item.id} onToggle={() => onOpen(openItem === item.id ? null : item.id)}
              existingSubmission={submissionByTaskId?.[item.id]}
              onSubmissionChange={onSubmissionChange} />
          ))}
        </div>
      )}
    </div>
  );
};

const ItemRow = ({ item, bucket, api, projectId, project, user: userProp, isOpen, onToggle, existingSubmission, onSubmissionChange }) => {
  // Defense-in-depth: pull user directly from auth context as a fallback
  // when the prop wasn't threaded (defends against cached old bundles +
  // any future regression of the "user is not defined" / prop-threading
  // class of bug). Prop wins when present so tests can still inject it.
  const { user: userCtx } = useAuth();
  const user = userProp || userCtx;
  const [media, setMedia] = useState([]);          // [{...uploaded, _previewUrl, _localKey}]
  const [uploading, setUploading] = useState(false);
  const [progressNote, setProgressNote] = useState(''); // "Uploading 2 of 3…"
  const [submitting, setSubmitting] = useState(false);
  const [note, setNote] = useState('');
  const [submitted, setSubmitted] = useState(false); // persistent until user adds more
  const [submittedAt, setSubmittedAt] = useState(null);
  const [err, setErr] = useState('');
  // Kind override — for concept_art only. The task's `parent_kind` is often
  // mistagged at task-creation time (e.g. "Candy House Interior" task created
  // as `character` when it's clearly an environment). Letting the artist
  // pick at upload time defends against that, and the override flows straight
  // into the NAS dual-save router so the file lands in the correct subfolder
  // (Character / Environment / Props).
  const [kindOverride, setKindOverride] = useState('');
  const [warn, setWarn] = useState('');           // duplicate warning
  const fileRef = useRef(null);

  // Initialise kind override from the task's parent_kind whenever a different
  // task is opened — gives the artist a sensible default, and they can still
  // correct it when the PM tagged the task wrong.
  useEffect(() => {
    setKindOverride((item?.parent_kind || '').toLowerCase());
  }, [item?.parent_code, item?.parent_kind]);

  // Cleanup blob URLs on unmount to avoid memory leaks
  useEffect(() => () => {
    media.forEach(m => m._previewUrl && URL.revokeObjectURL(m._previewUrl));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const onPickFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    // Duplicate detection by filename+size against already-attached media
    const existingKeys = new Set(media.map(m => `${m.filename}__${m.size}`));
    const fresh = [];
    const dupes = [];
    for (const f of files) {
      const k = `${f.name}__${f.size}`;
      if (existingKeys.has(k)) dupes.push(f.name);
      else { fresh.push(f); existingKeys.add(k); }
    }
    if (dupes.length) {
      setWarn(`Skipped duplicate${dupes.length > 1 ? 's' : ''}: ${dupes.slice(0, 3).join(', ')}${dupes.length > 3 ? `, +${dupes.length - 3}` : ''}`);
      setTimeout(() => setWarn(''), 4500);
    }
    if (!fresh.length) {
      if (fileRef.current) fileRef.current.value = '';
      return;
    }

    setUploading(true); setErr('');
    let currentFile = null;
    const startedAt = Date.now();
    try {
      let i = 0;
      for (const f of fresh) {
        i += 1;
        currentFile = f;
        setProgressNote(`Uploading ${i} of ${fresh.length} — ${f.name}`);
        const previewUrl = (f.type || '').startsWith('image/') ? URL.createObjectURL(f) : '';
        const fd = new FormData();
        fd.append('project_id', projectId);
        fd.append('file', f);
        const isVid = (f.type || '').startsWith('video/') || /\.(mp4|mov|webm|m4v|mkv)$/i.test(f.name);
        fd.append('preset', isVid ? 'web-720' : 'none');
        // Tell backend which stage/kind/code so it can dual-save into the right NAS subfolder.
        // Use the artist's explicit override (set via the "What kind is this?" picker)
        // when present; otherwise inherit the task's parent_kind from PM tagging.
        fd.append('stage', bucket?.stage || '');
        fd.append('kind', kindOverride || item?.parent_kind || '');
        fd.append('parent_code', item?.parent_code || '');
        // Chapter scoping — projects with episode/chapter context need the chapter folder prefix
        fd.append('chapter_name', project?.chapter_name || project?.name || '');
        const { data } = await api.post('/reviews/upload-media', fd);
        setMedia(prev => [...prev, { ...data, _previewUrl: previewUrl, _uploadedAt: Date.now() }]);
        // Adding new files invalidates a previous "submitted" badge — clearly a new round of work.
        setSubmitted(false);
      }
    } catch (e) {
      // Diagnostic error: surface HTTP status + file size + cause so the user
      // sees *why* it failed instead of a useless "Upload failed". Critical for
      // distinguishing proxy 413, backend 5xx, and pure network errors.
      setErr(formatUploadError(e, currentFile, startedAt));
    } finally {
      setUploading(false);
      setProgressNote('');
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const removeMedia = (idx) => setMedia(prev => {
    const m = prev[idx];
    if (m?._previewUrl) URL.revokeObjectURL(m._previewUrl);
    return prev.filter((_, i) => i !== idx);
  });

  const submit = async () => {
    if (!media.length) { setErr('Attach at least one file'); return; }
    setSubmitting(true); setErr('');
    try {
      // Strip local-only fields before posting
      const payload = media.map(({ _previewUrl, _uploadedAt, ...rest }) => rest);
      // ── Round 2+ resubmission path ──────────────────────────────────────
      // If the existing submission is in a revise/rejected state, this is a
      // round-bumping resubmission (v2, v3…) — call /resubmit which preserves
      // the previous round in `media_history` instead of deleting v1.
      const isRevisionUpload = existingSubmission && [
        'client_revise', 'client_rejected', 'pm_revise',
        'supervisor_revise', 'internal_revise',
      ].includes(existingSubmission.status);
      if (isRevisionUpload) {
        await api.post(`/reviews/${existingSubmission.id}/resubmit`, {
          media: payload,
          description: note || `Round ${(existingSubmission.round || 1) + 1} for ${item.parent_code || item.title}`,
        });
      } else {
        // Submission gate policy (Feb 2026): every artist submission goes
        // through Supervisor → PM → Client. PMs/admins/supervisors/CDs can
        // bypass internal review by sending `skip_internal: true`. Vendors
        // and freelancers cannot — backend enforces role check too.
        const canSkipInternal = ['master_admin', 'production_manager',
                                 'supervisor', 'creative_director'].includes(user?.role);
        await api.post('/reviews/submit', {
          project_id: projectId,
          kind: item.parent_kind === 'shot' ? 'shot' : 'asset',
          stage: bucket.stage,
          title: item.title,
          description: note || `${bucket.label} for ${item.parent_code || item.title}`,
          item_refs: [item.id],
          media: payload,
          ...(canSkipInternal ? { skip_internal: true } : {}),
        });
      }
      setSubmitted(true);
      setSubmittedAt(new Date());
      setNote('');
      // Clear local media so the row resets to a clean "submitted" state
      // (server now owns this round; new uploads = next round, started fresh)
      media.forEach(m => m._previewUrl && URL.revokeObjectURL(m._previewUrl));
      setMedia([]);
      // Refresh parent's submission map so the row pill flips to APPROVAL PENDING immediately
      if (onSubmissionChange) onSubmissionChange();
      // Note: keep media attached + row open so user sees what was submitted; they can clear/close manually.
    } catch (e) {
      // Same diagnostic pattern as the upload path so a 502/504/CF error
      // doesn't show as a generic "Submit failed" string. Surface the
      // status code + response body so we can tell whether the backend
      // crashed, the proxy timed out, or the API actually rejected us.
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      const detailStr = typeof detail === 'string'
        ? detail
        : (detail?.error || detail?.message || (detail && JSON.stringify(detail).slice(0, 200)) || '');
      // 409 = duplicate submission already exists for this item
      if (status === 409) {
        setErr('Already submitted — open the existing submission to revise, or delete it first from the Approvals inbox.');
      } else if (status === 502 || status === 504) {
        setErr(`Submit timed out (${status}). The backend is overloaded or restarting — wait 30 seconds and retry. ${detailStr}`);
      } else if (status) {
        setErr(`Submit failed (${status}). ${detailStr || 'No detail returned.'}`);
      } else if (e?.message) {
        setErr(`Submit failed: ${e.message}`);
      } else {
        setErr('Submit failed');
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Derive a live "approval state" for the row header pill. Priority:
  //   1. existingSubmission from the server (survives reloads, shows real client status)
  //   2. local `submitted` flag (only matters until next data refresh)
  const liveStatus = existingSubmission?.status || '';
  const liveDecision = existingSubmission?.client_decision || '';
  const approvalState = (() => {
    if (liveStatus === 'final_approved' || liveDecision === 'approved') return 'approved';
    if (liveDecision === 'revise' || liveStatus === 'client_revise')     return 'revise';
    if (liveDecision === 'rejected' || liveStatus === 'client_rejected') return 'rejected';
    if (existingSubmission || submitted) return 'pending';
    return null;
  })();
  // True when the existing submission is bouncing back to the artist for
  // a Round-2 (or later) re-upload. Drives the rose "UPLOAD REVISION" CTA
  // + the rejection-reason banner.
  const isRevisionMode = ['client_revise', 'client_rejected', 'pm_revise',
                          'supervisor_revise', 'internal_revise'].includes(liveStatus);
  const nextRound = (existingSubmission?.round || 1) + 1;
  const STATUS_PILL = {
    pending:  { label: 'APPROVAL PENDING', cls: 'bg-rose-500/20 text-rose-300 border-rose-500/40' },
    approved: { label: 'APPROVED',         cls: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40' },
    revise:   { label: 'CHANGES REQUESTED', cls: 'bg-amber-500/20 text-amber-300 border-amber-500/40' },
    rejected: { label: 'REJECTED',         cls: 'bg-rose-700/30 text-rose-200 border-rose-700/60' },
  };
  const pill = approvalState ? STATUS_PILL[approvalState] : null;

  const deleteSubmission = async (e) => {
    if (e) { e.stopPropagation(); e.preventDefault(); }
    if (!existingSubmission?.id) return;
    if (!window.confirm('Delete this submission? You can then upload a fresh round.')) return;
    try {
      await api.delete(`/reviews/submissions/${existingSubmission.id}`);
      setSubmitted(false);
      setSubmittedAt(null);
      // wipe locally attached media too — they belong to the deleted round
      media.forEach(m => m._previewUrl && URL.revokeObjectURL(m._previewUrl));
      setMedia([]);
      if (onSubmissionChange) onSubmissionChange();
    } catch (err) { alert(err.response?.data?.detail || 'Delete failed'); }
  };

  return (
    <div className="border-b border-zinc-800/40 last:border-0" data-testid={`item-${item.id}`}>
      <div className={`px-3 py-2 flex items-center gap-2 cursor-pointer ${isRevisionMode ? 'bg-rose-500/10 hover:bg-rose-500/15 border-l-2 border-rose-500' : 'hover:bg-zinc-800/30'}`} onClick={onToggle}>
        {isOpen ? <ChevronDown className="w-3 h-3 text-zinc-500" /> : <ChevronRight className="w-3 h-3 text-zinc-700" />}
        <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: bucket.color }} />
        <span className="text-[11px] text-zinc-300 flex-1">{item.title}</span>
        {item.assigned_to && <span className="text-[9px] text-emerald-400">assigned</span>}
        {/* Persistent approval-state pill on the row — survives reload, reflects live client gate. */}
        {pill && (
          <span className={`text-[9px] uppercase font-bold px-1.5 py-0.5 rounded border flex items-center gap-1 ${pill.cls}`}
            data-testid={`row-status-${item.id}`}
            title={existingSubmission ? `Submission "${existingSubmission.title}" · status=${liveStatus || 'pending'}` : 'Just submitted'}>
            {approvalState === 'approved' ? <CheckCircle2 className="w-2.5 h-2.5" /> : <AlertTriangle className="w-2.5 h-2.5" />}
            {pill.label}
          </span>
        )}
        {isRevisionMode && (
          <span className="text-[9px] uppercase font-bold px-1.5 py-0.5 rounded bg-rose-500 text-white flex items-center gap-1 animate-pulse"
                data-testid={`upload-revision-cta-${item.id}`}>
            <Upload className="w-2.5 h-2.5" /> UPLOAD v{nextRound}
          </span>
        )}
        {existingSubmission && !isRevisionMode && (
          <button onClick={deleteSubmission} title="Delete this submission and start over"
            data-testid={`row-delete-${item.id}`}
            className="text-[8px] uppercase font-medium px-1.5 py-0.5 rounded bg-rose-500/10 text-rose-300 hover:bg-rose-500/25 border border-rose-500/30 flex items-center gap-1">
            <X className="w-2.5 h-2.5" /> Delete
          </button>
        )}
        <span className={`text-[9px] uppercase px-1.5 py-0.5 rounded ${item.status === 'assigned' ? 'bg-blue-500/15 text-blue-400' : 'bg-zinc-800 text-zinc-500'}`}>
          {item.status}
        </span>
        {!isOpen && !approvalState && (
          <span className="text-[9px] text-amber-400 ml-1">+ Upload & Submit</span>
        )}
      </div>
      {isOpen && (
        <div className="px-3 py-3 bg-zinc-950/40 border-t border-zinc-800/40 space-y-2.5" onClick={e => e.stopPropagation()}>
          {/* ── Revision banner: shows the client's rejection note + previous round preview */}
          {isRevisionMode && (
            <div className="bg-rose-500/10 border border-rose-500/40 rounded-md p-3" data-testid={`revision-banner-${item.id}`}>
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-rose-400 flex-shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] uppercase tracking-wider font-bold text-rose-300 mb-1 flex items-center gap-2">
                    {liveStatus === 'client_rejected' ? 'Rejected' : 'Changes requested'}
                    <span className="text-zinc-500 normal-case">· upload v{nextRound} below — v{existingSubmission?.round || 1} stays in audit history</span>
                  </div>
                  {existingSubmission?.latest_client_note ? (
                    <p className="text-[12px] text-rose-100/90 leading-snug whitespace-pre-wrap" data-testid={`revision-note-${item.id}`}>
                      "{existingSubmission.latest_client_note}"
                    </p>
                  ) : (
                    <p className="text-[11px] text-zinc-500 italic">No specific note from the reviewer — open the Approvals inbox for details.</p>
                  )}
                </div>
              </div>
              {/* Mini-thumbnails of the previous round so the artist sees what's being replaced */}
              {(existingSubmission?.media || []).length > 0 && (
                <div className="mt-3 pt-2 border-t border-rose-500/20">
                  <p className="text-[9px] uppercase tracking-wider text-rose-300/70 mb-1.5">
                    Previous v{existingSubmission?.round || 1} ({(existingSubmission?.media || []).length} file{(existingSubmission?.media || []).length !== 1 ? 's' : ''}) — preserved in audit trail
                  </p>
                  <div className="flex flex-wrap gap-1">
                    {(existingSubmission.media || []).slice(0, 6).map((m, i) => (
                      <span key={i} className="inline-flex items-center gap-1 text-[9px] text-zinc-400 bg-zinc-900 border border-zinc-800 rounded px-1.5 py-0.5 font-mono"
                        data-testid={`prev-media-${item.id}-${i}`}>
                        {(m.type || '').startsWith('video') ? <Film className="w-2.5 h-2.5" /> : <ImageIcon className="w-2.5 h-2.5" />}
                        {(m.filename || '').slice(0, 24) || `file-${i + 1}`}
                      </span>
                    ))}
                    {(existingSubmission.media || []).length > 6 && (
                      <span className="text-[9px] text-zinc-500 px-1.5 py-0.5">+{(existingSubmission.media || []).length - 6} more</span>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
          {/* Upload area */}
          <div>
            {/* Concept Art kind override — defends against PMs creating tasks
                with the wrong kind tag. Artist picks here at upload time and
                the file lands in the correct NAS subfolder (Character /
                Environment / Props). Initial value mirrors the task's
                parent_kind so the friendly default is "what the PM said". */}
            {(bucket?.stage === 'concept_art') && (
              <div className="mb-2 bg-violet-500/5 border border-violet-500/20 rounded-sm px-3 py-2 flex items-center gap-2 flex-wrap" data-testid={`kind-picker-${item.id}`}>
                <span className="text-[10px] text-violet-300 font-medium">What kind is this?</span>
                {[
                  { v: 'character',   label: '👤 Character',   sub: 'Character/' },
                  { v: 'environment', label: '🏞️ Environment', sub: 'Environment/' },
                  { v: 'prop',        label: '⚙️ Prop',        sub: 'Props/' },
                ].map(opt => {
                  const active = (kindOverride || item?.parent_kind || '').toLowerCase().startsWith(opt.v.slice(0, 4));
                  return (
                    <button
                      key={opt.v}
                      type="button"
                      onClick={() => setKindOverride(opt.v)}
                      data-testid={`kind-${opt.v}-${item.id}`}
                      className={`h-7 px-2.5 text-[10px] font-medium rounded-sm border transition-colors ${active ? 'bg-violet-500/30 border-violet-400 text-violet-100' : 'bg-zinc-900 border-zinc-700 text-zinc-400 hover:bg-zinc-800 hover:text-zinc-200'}`}
                    >
                      {opt.label}
                    </button>
                  );
                })}
                <span className="text-[9px] text-zinc-500 ml-auto">
                  Will land in: <span className="font-mono text-cyan-400">{
                    (() => {
                      const k = (kindOverride || item?.parent_kind || '').toLowerCase();
                      if (k.startsWith('char')) return 'Character/';
                      if (k.startsWith('env') || k.startsWith('back') || k.startsWith('bg') || k.startsWith('loc')) return 'Environment/';
                      if (k.startsWith('prop')) return 'Props/';
                      return 'General/';
                    })()
                  }</span>
                </span>
              </div>
            )}
            <input ref={fileRef} type="file" multiple accept="image/*,video/*"
              onChange={onPickFiles} className="hidden" data-testid={`file-${item.id}`} />
            <div className="flex items-center gap-2">
              <button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid={`pick-${item.id}`}
                className="px-3 py-1.5 bg-amber-500/15 hover:bg-amber-500/25 text-amber-300 rounded-sm text-[10px] flex items-center gap-1.5 disabled:opacity-40">
                {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
                {uploading ? 'Uploading…' : 'Add image / video'}
              </button>
              <span className="text-[9px] text-zinc-400" data-testid={`media-count-${item.id}`}>
                {media.length} file{media.length !== 1 ? 's' : ''} attached
              </span>
              {progressNote && (
                <span className="text-[9px] text-amber-300 flex items-center gap-1" data-testid={`upload-progress-${item.id}`}>
                  <Loader2 className="w-2.5 h-2.5 animate-spin" /> {progressNote}
                </span>
              )}
            </div>
            {warn && (
              <div className="mt-1.5 flex items-center gap-1 text-[10px] text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded px-2 py-1" data-testid={`dup-warn-${item.id}`}>
                <AlertTriangle className="w-2.5 h-2.5" /> {warn}
              </div>
            )}
            {media.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1.5">
                {media.map((m, i) => {
                  const isImg = (m.type || '').startsWith('image') || /\.(jpg|jpeg|png|webp|gif)$/i.test(m.filename);
                  return (
                    <div key={i}
                      className="relative w-16 h-16 bg-zinc-900 border border-emerald-500/40 rounded-sm overflow-hidden flex items-center justify-center group"
                      title={`${m.filename} · ${(m.size / 1024).toFixed(0)} KB`}
                      data-testid={`thumb-${item.id}-${i}`}>
                      {isImg && m._previewUrl ? (
                        <img src={m._previewUrl} alt={m.filename} className="w-full h-full object-cover" />
                      ) : isImg ? (
                        <ImageIcon className="w-4 h-4 text-zinc-500" />
                      ) : (
                        <Film className="w-4 h-4 text-zinc-500" />
                      )}
                      {/* Tiny "uploaded" check on every successful upload */}
                      <span className="absolute bottom-0 left-0 right-0 bg-emerald-500/80 text-zinc-950 text-[8px] font-bold uppercase tracking-wider text-center py-0.5 flex items-center justify-center gap-0.5">
                        <CheckCircle2 className="w-2.5 h-2.5" /> Uploaded
                      </span>
                      <button onClick={() => removeMedia(i)}
                        className="absolute -top-1 -right-1 bg-rose-500 rounded-full p-0.5 hover:bg-rose-400">
                        <X className="w-2.5 h-2.5 text-white" />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          {/* Note */}
          <textarea value={note} onChange={e => setNote(e.target.value)} placeholder="Note for the client (optional)…"
            rows={2} data-testid={`note-${item.id}`}
            className="w-full p-2 bg-zinc-950 border border-zinc-800 rounded-sm text-[11px] text-white focus:outline-none" />
          {err && (
            <div className="flex items-center gap-1.5 text-[10px] text-rose-400">
              <AlertTriangle className="w-3 h-3" /> {err}
            </div>
          )}
          {/* Submit / Submitted banner */}
          <div className="flex items-center justify-between gap-2">
            {submitted ? (
              <div className="flex-1 flex items-center justify-between bg-emerald-500/10 border border-emerald-500/40 rounded-sm px-2.5 py-1.5"
                data-testid={`submitted-${item.id}`}>
                <span className="text-[11px] text-emerald-300 flex items-center gap-1.5">
                  <CheckCircle2 className="w-3.5 h-3.5" />
                  {isRevisionMode ? `v${nextRound} sent to client` : 'Submitted to client'}
                  {submittedAt && (
                    <span className="text-[9px] text-emerald-500 ml-1">
                      · {submittedAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  )}
                </span>
                <button onClick={() => { media.forEach(m => m._previewUrl && URL.revokeObjectURL(m._previewUrl)); setMedia([]); setSubmitted(false); setSubmittedAt(null); }}
                  className="text-[10px] text-zinc-400 hover:text-white px-2"
                  data-testid={`clear-${item.id}`}>
                  Clear & start new
                </button>
              </div>
            ) : (
              <>
                <span className="text-[10px] text-zinc-500">
                  {media.length > 0
                    ? `${media.length} ready to send${isRevisionMode ? ` as v${nextRound}` : ''}`
                    : isRevisionMode
                      ? `Pick files to upload v${nextRound}`
                      : 'No files yet'}
                </span>
                <button onClick={submit} disabled={!media.length || submitting} data-testid={`submit-${item.id}`}
                  className={`px-3 py-1.5 disabled:opacity-40 rounded-sm text-[10px] text-white font-medium flex items-center gap-1.5 ${
                    isRevisionMode
                      ? 'bg-gradient-to-r from-rose-500 to-orange-500 hover:from-rose-400 hover:to-orange-400'
                      : 'bg-gradient-to-r from-amber-500 to-rose-500 hover:from-amber-400 hover:to-rose-400'
                  }`}>
                  {submitting
                    ? <Loader2 className="w-3 h-3 animate-spin" />
                    : isRevisionMode
                      ? <Upload className="w-3 h-3" />
                      : <Send className="w-3 h-3" />}
                  {submitting
                    ? 'Sending…'
                    : isRevisionMode
                      ? `UPLOAD v${nextRound}`
                      : 'SUBMIT to Client'}
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
